import { State } from '@meraki-internal/state';
import { ICaremapNoteResponse } from '../model/NotesClient';
import { CaremapNotesModelFactory } from '../model/CaremapNotesModelFactory';
import { IFreeTierLimits } from '../../innerhive-plus/revenue-cat/IFreeTierLimits';
import { INoteFieldConflict, NoteConflictDetector } from './NoteConflictDetector';
import { IResourceChangedSocketMessage } from '../../web-sockets/SocketState';
import { isExpectedFetchError } from '../../support/isExpectedFetchError';

export class EditNoteFormViewModel extends State<Record<string, never>> {
    private baseNote: ICaremapNoteResponse;
    private pendingConflicts: INoteFieldConflict[] = [];
    private unsubscribers: (() => void)[] = [];

    constructor(
        private note: ICaremapNoteResponse,
        private factory: CaremapNotesModelFactory,
        private conflictDetector: NoteConflictDetector
    ) {
        super({ });
        this.baseNote = this.clone(note);
        this.subscribeToRemoteChanges();
    }

    private clone = (note: ICaremapNoteResponse): ICaremapNoteResponse => JSON.parse(JSON.stringify(note));

    private subscribeToRemoteChanges = () => {
        // a co-owner's edit arrives as web-sockets.message; an open editor needs it too.
        // idempotent (re-fetch + detect) per the BufferedEventBus replay contract.
        this.unsubscribers.push(this.factory.events.on('web-sockets.message', (msg) => {
            const message = msg as IResourceChangedSocketMessage;
            if (message?.resourceToRefresh === this.note.links.self?.href) {
                if (message.resourceChangeType === 'deleted') {
                    this.markDeletedRemotely();
                    return;
                }
                this.onRemoteResourceChanged().catch(err => {
                    // an aborted/transient fetch is expected here (the deleted case is handled inside
                    // onRemoteResourceChanged); alert only on genuine failures
                    if (!isExpectedFetchError(err)) {
                        this.factory.logger.error(`EditNoteFormViewModel websocket reconcile failed: ${err.toString()}`);
                    }
                });
            }
        }));
        // Slice B's foreground/reconnect catch-up may have advanced the open note's server version.
        this.unsubscribers.push(this.factory.events.on('ForegroundReconciler.reconciled', () => {
            this.onRemoteResourceChanged().catch(err => {
                // the open note being deleted by a co-owner (404) or an aborted fetch is expected;
                // alert only on genuine failures
                if (!isExpectedFetchError(err)) {
                    this.factory.logger.error(`EditNoteFormViewModel foreground reconcile failed: ${err.toString()}`);
                }
            });
        }));
    };

    dispose = () => {
        for (const unsubscribe of this.unsubscribers) {
            unsubscribe();
        }
        this.unsubscribers = [];
    };

    private onRemoteResourceChanged = async () => {
        let latest: ICaremapNoteResponse;
        try {
            latest = await this.factory.notesClient.getNote(this.note.links.self);
        } catch (err: any) {
            // the open note was deleted by a co-owner (404): surface it so the editor can tell the
            // user and close, rather than letting them keep editing a note that no longer exists.
            if (err && err.status === 404) {
                this.markDeletedRemotely();
                return;
            }
            throw err;
        }
        this.applyRemoteChange(latest);
    };

    private isDeletedRemotely = false;

    // true once the open note has been deleted by a collaborator; the editor reacts by alerting the
    // user and closing. Idempotent — repeated triggers (a deleted push then a foreground 404) are fine.
    wasDeletedRemotely = () => this.isDeletedRemotely;

    private markDeletedRemotely = () => {
        if (this.isDeletedRemotely) {
            return;
        }
        this.isDeletedRemotely = true;
        this.setState({ });
    };

    applyRemoteChange = (serverNote: ICaremapNoteResponse) => {
        const { merged, conflicts } = this.conflictDetector.detect({ base: this.baseNote, mine: this.note, theirs: serverNote });
        this.note = merged;
        // advance base to server for everything except still-conflicting fields (those keep their
        // old base so mine/theirs stays meaningful until the user resolves them)
        this.baseNote = this.clone(serverNote);
        for (const conflict of conflicts) {
            (this.baseNote[conflict.field] as unknown) = conflict.base;
            (this.note[conflict.field] as unknown) = conflict.mine;
        }
        this.pendingConflicts = conflicts;
        // A merge can erase the very change that made this dirty: the attachment I added is now on
        // both copies, so the note matches the server exactly. Leaving isDirty set would keep Save
        // enabled on a note with nothing to save, and pressing it PUTs a no-op that bumps the
        // version, wakes every collaborator over the websocket, writes an audit row and counts as
        // an edit in the tracking metrics.
        this.recomputeDirty();
        this.setState({ });
    };

    getDirtyFields = () => this.conflictDetector.getDirtyFields(this.baseNote, this.note);
    getPendingConflicts = () => this.pendingConflicts;

    resolveConflict = (field: INoteFieldConflict['field'], choice: 'mine' | 'theirs') => {
        const conflict = this.pendingConflicts.find(c => c.field === field);
        if (!conflict) {
            return;
        }
        (this.note[field] as unknown) = choice === 'mine' ? conflict.mine : conflict.theirs;
        (this.baseNote[field] as unknown) = choice === 'theirs' ? conflict.theirs : conflict.base;
        this.pendingConflicts = this.pendingConflicts.filter(c => c.field !== field);
        // Choosing THEIRS writes the same value into both copies, so resolving the last conflict
        // can leave the note identical to the server's. Without this, Save stays lit on a note with
        // nothing left to save — and this is the likeliest place for that, not the rarest.
        this.recomputeDirty();
        this.setState({ });
    };

    isValid = () => this.factory.validator.isValid(this.getNote());

    getNote = () => {
        return this.note;
    };

    setNote = (note: ICaremapNoteResponse) => {
        this.note = note;
        this.recomputeDirty();
        this.setState({ });
    };

    // Every place that CHANGES either copy of the note asks this rather than deciding for itself:
    // an edit, a remote merge and a conflict resolution disagreeing about what "dirty" means is
    // precisely how Save ends up enabled on an unchanged note, or disabled on a changed one.
    //
    // `save` is the deliberate exception. It does not ask, it asserts `false`, because having just
    // written the note to the server it knows the answer outright rather than inferring it from a
    // comparison that a clone could quietly perturb.
    //
    // Attachments count even though they are not conflictable — see
    // NoteConflictDetector.hasAttachmentChanges. Without them, adding a file and changing nothing
    // else left Save disabled forever.
    private recomputeDirty = () => {
        this.isDirty = this.getDirtyFields().length > 0
            || this.conflictDetector.hasAttachmentChanges(this.baseNote, this.note);
    };

    canSave = () => !this.isSaving && this.isValid() && this.isDirty && this.pendingConflicts.length === 0;

    isDirty = false;
    isSaving = false;

    save = async ({
        trackingEvent,
        trackingFrom,
        skipTracking
    }: {
        trackingEvent?: string;
        trackingFrom?: string;
        skipTracking?: boolean;
    } = {}, isRetry = false): Promise<void> => {
        this.isSaving = true;
        this.setState({ });

        try {
            await this.factory.forActiveCaremap().putNote(this.getNote());
            this.baseNote = this.clone(this.note);
            this.isDirty = false;

            if (!skipTracking) {
                this.trackSaved(trackingEvent, trackingFrom);
            }
        }
        catch (err: unknown) {
            const status = (err as { status?: number }).status;
            if (status === 409 && !isRetry) {
                // someone saved between our load and our save: reload, field-merge, and retry once
                // if nothing we touched conflicts; otherwise surface the conflicts for resolution.
                const latest = await this.factory.notesClient.getNote(this.note.links.self);
                this.applyRemoteChange(latest);
                if (this.pendingConflicts.length === 0) {
                    this.isSaving = false;
                    return this.save({ trackingEvent, trackingFrom, skipTracking }, true);
                }
                // intentional non-throw: the conflict is surfaced to the user via pendingConflicts
                // (state), which the editor renders as the resolution UI — not an error.
                return;
            }
            throw err;
        }
        finally {
            this.isSaving = false;
            this.setState({ });
        }
    };

    private trackSaved = (trackingEvent?: string, trackingFrom?: string,) => {
        this.factory.tracking.onUpdatedNote(this.getNote(), trackingEvent, trackingFrom);
    };

    addAttachmentRequiresInnerhivePlus = (limits: IFreeTierLimits) => {
        return this.factory.attachmentCountHelper.addAttachmentRequiresInnerhivePlus(limits, this.getNote());
    };

    // if not, then read only
    canEdit = () => {
        return this.note.links.self && (this.note.links.self.actions || []).includes('put-note');
    };

    canDelete = () => {
        return this.note.links.self && (this.note.links.self.actions || []).includes('delete-note');
    };

    delete = async (trackingFrom?: string) => {
        await this.factory.forActiveCaremap().deleteNote(this.getNote());
        this.factory.tracking.onDeletedNote(this.getNote(), trackingFrom);
    };
}
